import { useState, type ReactNode } from 'react';
import { PanelLeftIcon } from 'lucide-react';

import { Sheet, SheetContent } from '@/components/ui/sheet';
import { MAIN_CONTENT_ID, SkipLink } from '@/shared/components/SkipLink';
import { useMaxWidth, usePhoneWidth } from '@/shared/hooks/usePhoneWidth';
import { RaiseMark } from './RaiseMark';

import {
  resolveSidebarCollapsed,
  usePreferencesStore,
} from '@/shared/store/preferences';
import { useFocusModeStore } from '@/shared/store/focusMode';
import { useRailMinifyStore } from '@/shared/store/railMinify';
import {
  AlertStack,
  useNotificationStream,
  useReportTimezone,
} from '@/features/notifications';

/** Below this width the rail collapses unless the reader has chosen otherwise. */
const COLLAPSE_BREAKPOINT = 1024;

export interface ShellFrameProps {
  /**
   * The rail's contents. Given the collapsed state rather than reading it, because
   * collapse belongs to the frame and a second source for it is how two shells drift.
   */
  rail: (collapsed: boolean) => ReactNode;
  /** What sits in the top bar beside the collapse control. */
  topbar?: ReactNode;
  /**
   * On a phone, drawn on the menu button: a mark for what the bar has no room
   * to say (spec 260). The button is its containing block.
   */
  phoneMenuMark?: ReactNode;
  /** On a phone, the top of the navigation sheet, above the rail (spec 260). */
  phoneNavHeader?: ReactNode;
  children: ReactNode;
}

/**
 * The window this application is arranged in (spec 074 FR-017, FR-018).
 *
 * One frame, two shells: a workspace and the organization console. It exists **because
 * two concrete callers do**, and not before — the constitution's Seams Match Real
 * Variation gate forbids the abstraction otherwise, and constrains its shape: this takes
 * *content*, never configuration. There is no `variant` prop and there must not be one;
 * a frame that knows which shell it is rendering has the seam in the wrong place
 * (`specs/074-cost-surfaces-remediation/contracts/shell-frame.md`).
 *
 * The console had grown its own frame, structurally inverted from this one — a full-width
 * header above a short nav column, where the application puts a full-height rail with the
 * bar inset beside it. The brand block missing, identity moved, a 224px column against
 * 240px and no responsive collapse were not four decisions; they were consequences of
 * that inversion, and they are what this removes.
 *
 * Collapse is a device preference, so it is persisted rather than held in the address:
 * it is not something a reader would expect to travel in a shared link. The viewport
 * only decides the default, until the reader chooses.
 */
export function ShellFrame({
  rail,
  topbar,
  phoneMenuMark,
  phoneNavHeader,
  children,
}: ShellFrameProps) {
  const viewportIsNarrow = useMaxWidth(COLLAPSE_BREAKPOINT);
  // On a phone there is no rail at all (spec 173): even collapsed it took 56 of
  // 390px on every page, and expanded it sat beside the page and left it 130px.
  // The toggle opens the whole rail over the page instead, and that is not a
  // preference: it is open only while it is being used.
  const isPhone = usePhoneWidth();
  const [phoneNavOpen, setPhoneNavOpen] = useState(false);
  const preference = usePreferencesStore((state) => state.sidebarCollapsed);
  const toggleSidebar = usePreferencesStore((state) => state.toggleSidebar);
  const setSidebarCollapsed = usePreferencesStore(
    (state) => state.setSidebarCollapsed,
  );

  // A page in focus mode (the builder) shows the rail unless the person opens
  // the sidebar while there; their stored preference is left alone (spec 128).
  const focus = useFocusModeStore();
  // The notifications push channel, held once for whichever shell is showing
  // (spec 131 FR-002): the workspace shell and the console both sit in this frame.
  useNotificationStream();
  // The zone the morning digest is sent in (spec 134).
  useReportTimezone();
  /*
    A file preview is open (spec 140 FR-078). It is pinned to the right and
    wide, so the rail shows as icons only while it is up — the reader keeps the
    app frame in view and the preview reads as a preview rather than as a
    navigation to somewhere else. Last word, over both the reader's preference
    and focus mode, because it is a statement about what is on screen *now*;
    nothing is written to the stored preference, so closing the preview puts
    the rail back exactly as the reader left it.
  */
  const railMinified = useRailMinifyStore((state) => state.requests > 0);
  const collapsed =
    railMinified ||
    (focus.active
      ? !focus.expanded
      : resolveSidebarCollapsed(preference, viewportIsNarrow));

  return (
    <div className="bg-background relative flex h-screen w-full overflow-hidden">
      {/* First in the DOM, so it is the first thing Tab reaches on every page. */}
      <SkipLink />
      {isPhone ? (
        <Sheet open={phoneNavOpen} onOpenChange={setPhoneNavOpen}>
          <SheetContent
            side="left"
            showCloseButton={false}
            aria-label="Navigation"
            className="w-auto max-w-[85vw] border-0 sm:max-w-[85vw]"
            // A destination chosen is the rail's job done: close as the
            // page changes, as a phone's navigation drawer does.
            onClickCapture={(event) => {
              if ((event.target as Element).closest('a[href]')) {
                setPhoneNavOpen(false);
              }
            }}
          >
            <div className="flex h-full min-h-0 flex-col">
              {phoneNavHeader}
              <div className="flex min-h-0 flex-1">{rail(false)}</div>
            </div>
          </SheetContent>
        </Sheet>
      ) : (
        rail(collapsed)
      )}
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="border-border bg-background flex h-12 shrink-0 items-center gap-3 border-b px-3">
          {/*
            The control belongs to the frame, not to either shell's top bar — FR-018
            asks for the same behaviour *and the same control*, and two copies of a
            toggle is how "the same" stops being true.
          */}
          {/*
            Disabled while a preview is open (spec 140 FR-078, amended
            2026-10-14). `railMinified` forces `collapsed`, and the branches
            below read `collapsed` — so with no stored preference a click wrote
            `setSidebarCollapsed(!collapsed)` → `false`, persisting a
            preference *derived from the forced state*. FR-078's premise is
            that nothing is written, so closing the preview restores the rail
            as the reader left it; that write broke it silently and changed the
            rail on later visits. Disabling is the honest option over
            recomputing from the unforced value: while the preview is up the
            rail cannot expand at all, so a control that is enabled and
            announces `aria-expanded` yet moves nothing is the worse lie.
          */}
          <button
            type="button"
            disabled={railMinified}
            onClick={() => {
              if (isPhone) {
                setPhoneNavOpen(true);
                return;
              }
              // With no stored preference the displayed state came from the viewport,
              // so record the opposite of what is on screen rather than of the
              // (absent) preference.
              if (focus.active) {
                focus.setExpanded(collapsed);
              } else if (preference === null) {
                setSidebarCollapsed(!collapsed);
              } else {
                toggleSidebar();
              }
            }}
            aria-expanded={isPhone ? phoneNavOpen : !collapsed}
            aria-label={
              isPhone
                ? 'Open navigation'
                : railMinified
                  ? 'Expand sidebar (unavailable while a file preview is open)'
                  : collapsed
                    ? 'Expand sidebar'
                    : 'Collapse sidebar'
            }
            className="hover:bg-accent focus-visible:ring-ring touch-target inline-flex items-center justify-center rounded-md p-1.5 transition-colors focus-visible:ring-3 focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50 max-md:relative"
          >
            <PanelLeftIcon
              aria-hidden="true"
              className="size-4"
              strokeWidth={1.5}
            />
            {isPhone ? phoneMenuMark : null}
          </button>
          {topbar}
          {/*
            Not on a phone (spec 260): the mark was one of five things beside
            the page's name, which left the name its first letter. The rail the
            menu opens carries the brand.
          */}
          {isPhone ? null : <RaiseMark />}
        </header>
        {/*
          `scrollbar-gutter-stable` because this element is the one that scrolls
          for every page in the shell. Its bar comes and goes with the content —
          a search that narrows a list, a filter that empties one — and without a
          reserved gutter the entire page shifts sideways by the bar's width each
          time, which is at its worst while typing, when the text you are reading
          moves under you keystroke by keystroke.
        */}
        {/*
          `relative` is load-bearing, not decoration. Tailwind's `sr-only` is
          `position: absolute`, and this pane is the only thing clipping the page's
          content — but a static ancestor is not a containing block, so every `sr-only`
          span inside it was laid out against the *initial* containing block instead.
          Each one sat at its static position deep inside the scrolled content and
          stretched the document to reach it, so the whole page scrolled and the rail
          scrolled away with it, leaving blank space below.

          Positioning this element makes it the containing block, so those spans are
          clipped here like everything else. Any page with enough rows hit this; the
          organization console's two tables, which carry an `sr-only` state label per
          row, hit it hardest.
        */}
        <main
          id={MAIN_CONTENT_ID}
          tabIndex={-1}
          className="scrollbar-gutter-stable relative flex min-h-0 flex-1 flex-col overflow-auto"
        >
          {children}
        </main>
      </div>
      {/* What the stream says needs attention now (spec 136). */}
      <AlertStack />
    </div>
  );
}
